import type { ReactNode } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useIsDesktop } from '../../hooks/useMediaQuery';
import type { BubbleAnchor } from './bubbleAnchor';
import { useTargetPosition } from './useTargetPosition';
import { useTourStepPresence } from '../../stores/tour-presence.store';

/** Below the compact header (60px) + 12px, until the target is measured. */
const HEADER_FALLBACK_TOP = 72;
/** The bubble's right edge (right-4) and the tail's half width. */
const BUBBLE_RIGHT = 16;
const TAIL_HALF = 8;

interface OnboardingSpeechBubbleProps {
  children: ReactNode;
  visible: boolean;
  maxWidth?: number;
  onSkip?: () => void;
  /** Where to anchor the bubble. 'bottom-right' (default) sits above Empy button.
   *  'top-right' renders below the desktop nav bar with tail pointing up.
   *  'header' renders just below `target` (a launcher or the avatar docked
   *  in the compact header below 1280) with its tail up, under the target.
   *  Pick it with shellBubbleAnchor() (./bubbleAnchor). */
  anchor?: BubbleAnchor;
  /** The selector a 'header' bubble points at. */
  target?: string;
}

export default function OnboardingSpeechBubble({
  children,
  visible,
  maxWidth,
  onSkip,
  anchor = 'bottom-right',
  target,
}: OnboardingSpeechBubbleProps) {
  const isDesktop = useIsDesktop();
  // A tour step is on screen: toasts keep out of its way (app/toastConfig.ts).
  useTourStepPresence(visible);
  const isHeaderAnchored = anchor === 'header';
  const targetRect = useTargetPosition(isHeaderAnchored ? target : undefined);

  // Desktop default is wider since there's more screen real estate
  const resolvedMaxWidth = maxWidth ?? (isDesktop ? 360 : 280);

  const isTopAnchored = (anchor === 'top-right' && isDesktop) || isHeaderAnchored;

  // Header-anchored: 12px under the target (never above the screen's top,
  // when the header has scrolled away), the tail centred under it.
  let headerTop = HEADER_FALLBACK_TOP;
  let tailRight = 24; // right-6, as under the desktop bar
  if (isHeaderAnchored && targetRect && targetRect.width > 0) {
    const viewport = document.documentElement.clientWidth || window.innerWidth;
    const targetCentre = targetRect.left + targetRect.width / 2;
    headerTop = Math.max(8, Math.round(targetRect.top + targetRect.height + 12));
    tailRight = Math.round(
      Math.min(
        Math.max(viewport - BUBBLE_RIGHT - targetCentre - TAIL_HALF, 12),
        resolvedMaxWidth - 28,
      ),
    );
  }

  // Desktop: the Empy launcher floats at bottom-16 (64px) right-4 (16px) and
  // the bubble sits above it. Below 1280 the launcher is docked in the
  // layout's compact header (redesign PR3), and a bubble about it is
  // 'header'-anchored; any other bubble keeps its place above the bottom nav
  // and its raised E (196px), clear of the page's actions.
  const bottomOffset = isDesktop ? 116 : 196;

  return (
    <AnimatePresence>
      {visible ? (
        <motion.div
          initial={{ opacity: 0, y: isTopAnchored ? -12 : 12, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: isTopAnchored ? -8 : 8, scale: 0.97 }}
          transition={{ duration: 0.25, ease: 'easeOut' }}
          className="fixed z-[205] right-4"
          style={{
            ...(isHeaderAnchored
              ? { top: headerTop }
              : isTopAnchored
                ? { top: 76 } // Below the 64px desktop nav bar + 12px spacing
                : { bottom: bottomOffset }),
            maxWidth: `min(${resolvedMaxWidth}px, calc(100vw - 32px))`,
          }}
        >
          {/* CSS tail pointing up toward the nav bar or the docked target (top-anchored only) */}
          {isTopAnchored ? (
            <div
              className="absolute -top-2 w-0 h-0"
              style={{
                right: tailRight,
                borderLeft: '8px solid transparent',
                borderRight: '8px solid transparent',
                borderBottom: '8px solid var(--ee-surface)',
                filter: 'drop-shadow(0 -1px 1px rgba(0,0,0,0.05))',
              }}
            />
          ) : null}

          {/* Speech bubble body */}
          <div
            className={`bg-(--ee-surface) rounded-2xl shadow-xl border border-(--ee-tw-gray-100-line) p-4 cursor-default select-none ${
              isHeaderAnchored ? '' : isTopAnchored ? 'rounded-tr-sm' : 'rounded-br-sm'
            }`}
          >
            {children}

            {onSkip ? (
              <button
                onClick={onSkip}
                className="mt-3 w-full py-2 rounded-xl text-sm text-(--ee-tw-gray-500) hover:bg-(--ee-tw-gray-100-bg) transition-colors"
              >
                Skip Tour
              </button>
            ) : null}
          </div>

          {/* CSS tail pointing down-right toward Empy button (bottom-anchored only) */}
          {!isTopAnchored && (
            <div
              className="absolute -bottom-2 right-3 w-0 h-0"
              style={{
                borderLeft: '8px solid transparent',
                borderRight: '8px solid transparent',
                borderTop: '8px solid var(--ee-surface)',
                filter: 'drop-shadow(0 1px 1px rgba(0,0,0,0.05))',
              }}
            />
          )}
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}
